// Pin-capacity policy for interactive edge connects (ENG-877).
//
// ONE WIRE PER INPUT PIN. Connecting to an input that already has a wire
// DISPLACES that wire — always, on every graph this policy governs, for exec
// and data alike. Exec OUTPUTS are single-capacity too; data outputs fan out.
//
//   - Any INPUT pin holds ONE wire. A new connect replaces what fed it.
//   - EXEC output pins hold ONE wire: two outgoing exec wires would double-fire
//     the chain in EventRuntime, which walks every matching edge.
//   - DATA output pins fan out freely — one value, many readers.
//
// *** DO NOT "FIX" EXEC INPUTS BACK TO FAN-IN. ***
// This module shipped saying exec inputs accept many wires, "several chains may
// converge on one node", with Unreal cited as the authority and a unit test
// asserting it. Unreal does allow that; THIS PROJECT DOES NOT, and that is
// Andres's explicit and repeatedly-restated decision — the rule has been
// reverted several times by people who read the old comment, saw the passing
// test, and concluded fan-in was intended. It is not. A node whose exec input
// is fed by three wires fires three times and reads as one node that
// mysteriously runs in triplicate; the graph no longer says what order things
// happen in. If you want two chains to reach one node, the node needs two
// input PINS (that is exactly why Rendezvous has execInA / execInB), or the
// upstream chains need to be joined explicitly.
//
// Exec pins are identified by the repo-wide handle convention: every exec
// handle id starts with "exec" (execIn / execOut / execTrue / execFalse /
// execStop / execFinished / execProgress — see src/components/nodes/*).
//
// *** WHAT THIS POLICY MUST NEVER GOVERN: the state-machine TRANSITION canvas.
// A state is reached from many other states by design — the shipped blueprints
// hold 41 such pins (`f_talking::in` has six) and every one is correct. That
// canvas has its own onConnect in StateMachineEditor.jsx and must keep it; its
// `in`/`out` handles are not exec-prefixed, so routing it through here would
// silently delete transitions. A state's INNER graph is a different thing and
// is single-capacity already (StateMachineEditor's other onConnect).
//
// Callers: BlueprintEditor (body/face), EventGraph (main / function / group),
// TransitionRuleEditor. Pure function so the policy is unit-testable: returns
// the edge list with the displaced wires removed; the caller then addEdge()s
// the new one. The MCP server enforces the same rule on its own `connect` tool
// — a rule only the UI honours is not a rule.

export function isExecHandle(handleId) {
  return typeof handleId === 'string' && handleId.startsWith('exec')
}

export function dropDisplacedEdges(edges, params) {
  if (!Array.isArray(edges)) return []
  if (!params) return edges
  const execWire = isExecHandle(params.targetHandle)
  return edges.filter(e => {
    // The INPUT being connected holds one wire — whatever fed it is displaced.
    if (e.target === params.target && e.targetHandle === params.targetHandle) return false
    // An exec OUTPUT holds one wire too, so re-routing flow moves it rather
    // than forking it. Matched on the source handle, so a data wire leaving the
    // same node is never touched.
    if (execWire && e.source === params.source && e.sourceHandle === params.sourceHandle) return false
    return true
  })
}
